/**
 * View renderer - state subscription & DOM update orchestrator
 * This file coordinates all view modules
 */

import type { GameCore } from '@app-games/core';
import type { GameState, GameAction } from '../game/index.js';
import { getTotalCps, getTapPower } from '../game/index.js';
import { getCurrentRank } from '../game/data/ranks.js';
import { formatNumber, formatCps } from '../utils/format.js';
import { t } from '../i18n/index.js';

export type Tab = 'main' | 'upgrade' | 'prestige' | 'achievement' | 'mission' | 'domination' | 'agent' | 'infiltration' | 'settings';

export interface ViewState {
  currentTab: Tab;
  upgradeSubTab: 'click' | 'special';
  prestigeSubTab: 'p1' | 'p2';
  achFilter: string;
  conquestSelectedCountry: string | null;
}

let viewState: ViewState = {
  currentTab: 'main',
  upgradeSubTab: 'click',
  prestigeSubTab: 'p1',
  achFilter: 'all',
  conquestSelectedCountry: null,
};

let core: GameCore<GameState, GameAction>;

export function getViewState(): ViewState { return viewState; }
export function getCore(): GameCore<GameState, GameAction> { return core; }

/** Dirty flag: set when screen content needs full re-render */
let screenDirty = true;

export function setViewState(partial: Partial<ViewState>) {
  // When switching away from domination tab, reset conquest detail view
  // so the user doesn't get stuck in a country detail view when returning
  if (partial.currentTab && partial.currentTab !== 'domination' && viewState.conquestSelectedCountry !== null) {
    partial.conquestSelectedCountry = null;
  }
  viewState = { ...viewState, ...partial };
  screenDirty = true;
  requestRender();
}

export function initRenderer(gameCore: GameCore<GameState, GameAction>) {
  core = gameCore;
}

/** Registered render callbacks with tab scope */
interface RenderCallback {
  fn: () => void;
  /** If set, this callback is only called on every frame for these tabs */
  alwaysTabs?: Tab[];
}

let renderEntries: RenderCallback[] = [];

export function onRender(cb: () => void, opts?: { alwaysTabs?: Tab[] }) {
  renderEntries.push({ fn: cb, alwaysTabs: opts?.alwaysTabs });
}

let renderScheduled = false;
export function requestRender() {
  if (renderScheduled) return;
  renderScheduled = true;
  requestAnimationFrame(() => {
    renderScheduled = false;
    renderAll();
  });
}

/** Request a screen-content re-render on the next frame */
export function markScreenDirty() {
  screenDirty = true;
}

function renderAll() {
  updateHUD();

  const tab = viewState.currentTab;

  if (screenDirty) {
    // Clear htmlCache for screen-content so setInnerHTML() won't skip DOM updates
    // after another screen wrote container.innerHTML directly (bypassing cache)
    const sc = document.getElementById('screen-content');
    if (sc) htmlCache.delete(sc);

    // Full re-render: run all callbacks
    screenDirty = false;
    for (const entry of renderEntries) entry.fn();
  } else {
    // Tick-only: only run callbacks that opted-in for the current tab
    for (const entry of renderEntries) {
      if (entry.alwaysTabs?.includes(tab)) entry.fn();
    }
  }
}

function updateHUD() {
  const state = core.getState();
  const ipEl = document.getElementById('hud-ip');
  const cpsEl = document.getElementById('hud-cps');
  const gcEl = document.getElementById('hud-gc');
  const coresEl = document.getElementById('hud-cores');
  const docsEl = document.getElementById('hud-docs');
  const rankEl = document.getElementById('hud-rank');

  if (ipEl) ipEl.textContent = formatNumber(state.ip);
  if (cpsEl) cpsEl.textContent = formatCps(getTotalCps(state));
  if (gcEl) gcEl.textContent = formatNumber(state.gc);
  if (coresEl) coresEl.textContent = formatNumber(state.cores);
  if (docsEl) docsEl.textContent = String(state.classifiedDocs);
  if (rankEl) {
    const rank = getCurrentRank(state.rank ?? 'E');
    if (rank) rankEl.textContent = t(rank.nameKey);
  }

  // Update boost bar
  updateBoostBar(state);
}

/** Cache key for boost bar to avoid unnecessary innerHTML rewrites */
let lastBoostBarKey = '';

function updateBoostBar(state: GameState) {
  const bar = document.getElementById('boost-bar');
  if (!bar) return;
  const now = Date.now();
  const active = state.activeBoosts.filter(b => now < b.endsAt);

  if (active.length === 0) {
    if (lastBoostBarKey !== 'empty') {
      lastBoostBarKey = 'empty';
      bar.innerHTML = '';
    }
    return;
  }

  // Build key from boost types and remaining seconds (changes only once per second)
  const key = active.map(b => `${b.type}:${Math.ceil((b.endsAt - now) / 1000)}`).join('|');
  if (key === lastBoostBarKey) return;
  lastBoostBarKey = key;

  const html = active.map(b => {
    const remaining = Math.ceil((b.endsAt - now) / 1000);
    return `<span class="boost-pill">${b.type} ${remaining}s</span>`;
  }).join('');
  bar.innerHTML = html;
}

/** Show a toast notification with optional category label */
export function showToast(message: string, category?: string) {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.className = 'toast';
  if (category) {
    el.innerHTML = `<span class="toast-category">${category}</span> ${message}`;
  } else {
    el.textContent = message;
  }
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

// === FLOAT TEXT POOL ===
const FLOAT_POOL_SIZE = 20;
const floatPool: HTMLDivElement[] = [];
let floatPoolReady = false;

function ensureFloatPool() {
  if (floatPoolReady) return;
  floatPoolReady = true;
  for (let i = 0; i < FLOAT_POOL_SIZE; i++) {
    const el = document.createElement('div');
    el.style.display = 'none';
    el.dataset.floatActive = '0';
    document.body.appendChild(el);
    el.addEventListener('animationend', () => {
      el.style.display = 'none';
      el.dataset.floatActive = '0';
    });
    floatPool.push(el);
  }
}

/** Show floating damage text (pooled — no DOM create/remove) */
export function showFloatText(x: number, y: number, text: string, isCrit = false) {
  ensureFloatPool();
  // Find an inactive element in the pool
  let el: HTMLDivElement | null = null;
  for (const p of floatPool) {
    if (p.dataset.floatActive === '0') {
      el = p;
      break;
    }
  }
  if (!el) return; // All pool elements in use — skip to prevent GC pressure
  el.dataset.floatActive = '1';
  el.className = isCrit ? 'float-text float-text-crit' : 'float-text';
  el.textContent = text;
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  el.style.display = '';
}

/** Dispatch helper */
export function dispatch(action: GameAction) {
  core.dispatch(action);
  // TICK only updates HUD; other actions trigger full screen re-render
  if (action.type !== 'TICK') {
    screenDirty = true;
  }
  requestRender();
}

/** Get current state shorthand */
export function getState(): GameState {
  return core.getState();
}

/**
 * Set innerHTML only when content actually changed.
 * Prevents unnecessary DOM destruction/recreation that causes flicker.
 */
const htmlCache = new WeakMap<HTMLElement, string>();
export function setInnerHTML(el: HTMLElement, html: string): boolean {
  if (htmlCache.get(el) === html) return false;
  htmlCache.set(el, html);
  el.innerHTML = html;
  return true;
}

/** Show a modal popup for ad reward confirmation */
export function showAdRewardPopup(
  title: string,
  description: string,
  onConfirm: () => void,
  confirmText?: string,
  cancelText?: string,
) {
  // Remove existing modal if any
  document.querySelector('.modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-title">${title}</div>
      <div class="modal-body">${description.replace(/\n/g, '<br>')}</div>
      <div class="modal-btns">
        <button class="modal-btn secondary" id="ad-modal-cancel">${cancelText || t('cancel')}</button>
        <button class="modal-btn primary" id="ad-modal-confirm">\u{1F4FA} ${confirmText || t('confirm')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.getElementById('ad-modal-cancel')?.addEventListener('click', () => overlay.remove());
  document.getElementById('ad-modal-confirm')?.addEventListener('click', () => {
    overlay.remove();
    onConfirm();
  });
}

/** Show a conquest completion celebration popup */
export function showConquestPopup(flag: string, countryName: string) {
  document.querySelector('.modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal conquest-popup">
      <div class="conquest-popup-flag">${flag}</div>
      <div class="modal-title">${t('cq_popup_title')}</div>
      <div class="modal-body">
        <b>${countryName}</b> ${t('cq_popup_desc')}
      </div>
      <div class="modal-btns">
        <button class="modal-btn primary" id="conquest-popup-ok">${t('confirm')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.getElementById('conquest-popup-ok')?.addEventListener('click', () => overlay.remove());
}

/** Show a rank promotion celebration popup */
export function showRankPopup(
  prevGrade: string,
  prevName: string,
  prevTap: number,
  newGrade: string,
  newName: string,
  newTap: number,
) {
  document.querySelector('.modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal rank-popup">
      <div class="rank-popup-badge">\u2B06</div>
      <div class="modal-title">${t('rank_promoted')}</div>
      <div class="rank-popup-transition">
        <span class="rank-old">${prevGrade} ${prevName}</span>
        <span class="rank-arrow">\u279C</span>
        <span class="rank-new">${newGrade} ${newName}</span>
      </div>
      <div class="rank-popup-bonus">
        <div class="rank-bonus-row">TAP x${prevTap} \u279C <b>x${newTap}</b></div>
      </div>
      <div class="modal-btns">
        <button class="modal-btn primary" id="rank-popup-ok">${t('confirm')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.getElementById('rank-popup-ok')?.addEventListener('click', () => overlay.remove());
}

/** Show a prestige confirmation popup */
export function showPrestigeConfirm(
  title: string,
  rewardText: string,
  warningText: string,
  onConfirm: () => void,
) {
  document.querySelector('.modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal prestige-confirm-popup">
      <div class="modal-title">${title}</div>
      <div class="prestige-confirm-reward">${rewardText}</div>
      <div class="prestige-confirm-warning">${warningText}</div>
      <div class="modal-btns">
        <button class="modal-btn secondary" id="prestige-confirm-cancel">${t('cancel')}</button>
        <button class="modal-btn primary" id="prestige-confirm-ok">${t('confirm')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.getElementById('prestige-confirm-cancel')?.addEventListener('click', () => overlay.remove());
  document.getElementById('prestige-confirm-ok')?.addEventListener('click', () => {
    overlay.remove();
    onConfirm();
  });
}

export { getTotalCps, getTapPower };
